import axios from "axios";
import { Activity, Radar } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const systems = [
  {
    id: "payload",
    label: "PAYLOAD BAY",
    x: "50%",
    y: "49%",
    title: "Climate-Locked Payload Bay",
    text: "Vacuum-sealed thermal lockers keep organic perishables and quantum-battery components stable in the same armored bay.",
    status: "SEALED",
    stats: { CAPACITY: 92, SHIELDING: 88, RESPONSE: 64 },
  },
  {
    id: "repulsors",
    label: "OMNI REPULSORS",
    x: "20%",
    y: "23%",
    title: "Omni-Directional Repulsor Pads",
    text: "Four independent lift arrays keep the VTOL chassis stable during vertical drops, evasive turns, and hostile-sector landings.",
    status: "BALANCED",
    stats: { LIFT: 95, STABILITY: 90, RESPONSE: 82 },
  },
  {
    id: "defense",
    label: "DEFENSE ARRAY",
    x: "79%",
    y: "49%",
    title: "Non-Lethal Defense Array",
    text: "Stasis emitters, photonic grids, repulsion waves, and sticky-foam pods are positioned around the chassis for immediate interception response.",
    status: "ARMED",
    stats: { COVERAGE: 86, DETERRENT: 91, RESPONSE: 96 },
  },
  {
    id: "navigation",
    label: "NAVIGATION CORE",
    x: "50%",
    y: "17%",
    title: "Sub-Quantum Navigation Core",
    text: "Real-time routing bypasses irradiated dead-zones, subterranean hazards, and corrupted relay signals before they can trap the courier.",
    status: "TRACKING",
    stats: { PRECISION: 97, FORECAST: 89, RESPONSE: 78 },
  },
  {
    id: "reactor",
    label: "REACTOR CORE",
    x: "50%",
    y: "28%",
    title: "Twin-Cell Fusion Reactor",
    text: "Redundant micro-fusion cells share load across the chassis. If one cell drops, the courier still makes the delivery window.",
    status: "OPTIMAL",
    stats: { OUTPUT: 94, REDUNDANCY: 100, RESPONSE: 71 },
  },
  {
    id: "optics",
    label: "OPTIC ARRAY",
    x: "64%",
    y: "14%",
    title: "Long-Range Optic Array",
    text: "Multi-spectrum scopes read heat, motion, and transponder echoes three sectors out — the same eyes that made Dolo a champion.",
    status: "SCANNING",
    stats: { RANGE: 93, CLARITY: 87, RESPONSE: 84 },
  },
  {
    id: "harpoon",
    label: "HARPOON MOUNT",
    x: "8%",
    y: "50%",
    title: "Pneumatic Harpoon Mount",
    text: "Wing-mounted harpoon launcher for anchoring cliffside drops and discouraging gangers. Salvage-grade, runner-approved.",
    status: "LOADED",
    stats: { FORCE: 89, ACCURACY: 81, RESPONSE: 93 },
  },
  {
    id: "servos",
    label: "SERVO FRAME",
    x: "50%",
    y: "72%",
    title: "Reinforced Servo Frame",
    text: "Load-bearing servos borrowed from AL Riotball striker chassis tech — smash through barricades, land hard, keep moving.",
    status: "TORQUED",
    stats: { STRENGTH: 96, ENDURANCE: 85, RESPONSE: 76 },
  },
];

const telemetrySeed = [
  { id: "reactor", label: "REACTOR OUTPUT", value: 94, unit: "%", min: 87, max: 99 },
  { id: "signal", label: "SIGNAL STRENGTH", value: 78, unit: "%", min: 61, max: 99 },
  { id: "servo", label: "SERVO TEMP", value: 421, unit: "K", min: 408, max: 441 },
  { id: "hull", label: "HULL INTEGRITY", value: 96, unit: "%", min: 91, max: 99 },
];

const feedPool = [
  "UNIT 07 cleared Sector 4 with full payload integrity",
  "RIFF signed a Black Ops contract — night window only",
  "New runner enlisted into the MK-VII fleet registry",
  "GOJI reported vulture activity over the Sector 9 cliffs",
  "LIL GIRL delivered medical crates to Safe Zone B early",
  "KOJI back on active roster after servo refit",
  "DRONE BOY mapped a new air shadow over Sector 12",
  "SABLE escorted a water convoy through pirate corridor",
  "AGAD logged zero noise violations across the mesh tonight",
  "CINDERJACK broke the Ash Falls delivery record by 4 minutes",
];

export function CourierSchematic() {
  const [activeId, setActiveId] = useState("payload");
  const [telemetry, setTelemetry] = useState(telemetrySeed);
  const [feed, setFeed] = useState(() => feedPool.slice(0, 3));
  const [fleet, setFleet] = useState(240);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const feedIndex = useRef(3);
  const active = systems.find((item) => item.id === activeId) || systems[0];

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/enlist/count`)
      .then(({ data }) => {
        if (alive && typeof data.count === "number") setFleet(240 + data.count);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTelemetry((current) =>
        current.map((item) => {
          const drift = Math.round((Math.random() - 0.45) * 5);
          return { ...item, value: Math.min(item.max, Math.max(item.min, item.value + drift)) };
        }),
      );
    }, 2600);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setFeed((current) => {
        const next = feedPool[feedIndex.current % feedPool.length];
        feedIndex.current += 1;
        return [next, ...current].slice(0, 4);
      });
    }, 7000);
    return () => window.clearInterval(id);
  }, []);

  const runDiagnostics = () => {
    if (scanning) return;
    setScanning(true);
    setScanResult(null);
    systems.forEach((system, index) => {
      window.setTimeout(() => setActiveId(system.id), index * 420);
    });
    window.setTimeout(() => {
      setScanning(false);
      setScanResult("ALL SYSTEMS NOMINAL — MK-VII CLEARED FOR DEPLOYMENT");
      window.setTimeout(() => setScanResult(null), 4200);
    }, systems.length * 420 + 500);
  };

  const now = new Date();
  const stamp = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  return (
    <section className="fleet-dossier" data-testid="fleet-dossier-section">
      <SectionHeading
        eyebrow="FLEET DOSSIER / MK-VII SCHEMATIC"
        title="Interactive courier systems map."
        text="Eight live systems on the board. Hover or tap a node to inspect it, run a full diagnostic sweep, and watch the fleet telemetry breathe in real time."
      />

      <Reveal className="fleet-telemetry" data-testid="fleet-telemetry">
        {telemetry.map((item) => (
          <div className="telemetry-cell" data-testid={`telemetry-${item.id}`} key={item.id}>
            <small>{item.label}</small>
            <strong key={item.value}>
              {item.value}
              {item.unit}
            </strong>
            <span className="telemetry-bar">
              <span style={{ width: `${((item.value - item.min) / (item.max - item.min)) * 100}%` }} />
            </span>
          </div>
        ))}
        <div className="telemetry-cell telemetry-fleet" data-testid="fleet-strength">
          <small>FLEET STRENGTH</small>
          <strong key={fleet}>{fleet} UNITS</strong>
          <span className="telemetry-live">
            <i /> LIVE REGISTRY
          </span>
        </div>
      </Reveal>

      <Reveal className="fleet-dossier-panel" delay={0.08}>
        <div className="schematic-stage" data-testid="mk7-schematic-diagram">
          <svg viewBox="0 0 720 480" role="img" aria-label="Apex-Courier MK-VII technical schematic">
            <defs>
              <linearGradient id="hull" x1="0" x2="1">
                <stop offset="0" stopColor="#161616" />
                <stop offset="0.52" stopColor="#050505" />
                <stop offset="1" stopColor="#1c1c1c" />
              </linearGradient>
            </defs>
            <path className="schematic-wing" d="M110 150 L270 205 L270 275 L110 330 L40 240 Z" />
            <path className="schematic-wing" d="M610 150 L450 205 L450 275 L610 330 L680 240 Z" />
            <path className="schematic-hull" d="M255 120 L465 120 L545 240 L465 360 L255 360 L175 240 Z" />
            <path className="schematic-spine" d="M360 95 L410 170 L410 310 L360 385 L310 310 L310 170 Z" />
            <rect className="schematic-payload" x="292" y="184" width="136" height="112" rx="18" />
            <circle className="schematic-repulsor" cx="170" cy="150" r="42" />
            <circle className="schematic-repulsor" cx="170" cy="330" r="42" />
            <circle className="schematic-repulsor" cx="550" cy="150" r="42" />
            <circle className="schematic-repulsor" cx="550" cy="330" r="42" />
            <rect className="schematic-defense" x="230" y="205" width="42" height="70" rx="10" />
            <rect className="schematic-defense" x="448" y="205" width="42" height="70" rx="10" />
            <circle className="schematic-core" cx="360" cy="128" r="24" />
            <path className="schematic-line" d="M360 128 L360 184" />
            <path className="schematic-line" d="M292 240 L230 240" />
            <path className="schematic-line" d="M428 240 L448 240" />
          </svg>
          {scanning ? <div className="schematic-sweep" aria-hidden="true" /> : null}
          {systems.map((system) => (
            <button
              type="button"
              key={system.id}
              className={`schematic-hotspot ${activeId === system.id ? "is-active" : ""}`}
              style={{ left: system.x, top: system.y }}
              onClick={() => setActiveId(system.id)}
              onMouseEnter={() => setActiveId(system.id)}
              data-testid={`schematic-hotspot-${system.id}`}
              aria-label={`Inspect ${system.label}`}
              aria-pressed={activeId === system.id}
            >
              <span />
            </button>
          ))}
        </div>

        <div className="fleet-system-panel" data-testid="active-fleet-system-detail">
          <div className="fleet-system-head">
            <span>{active.label}</span>
            <b className="fleet-system-status">{active.status}</b>
          </div>
          <h3>{active.title}</h3>
          <p>{active.text}</p>
          <div className="fleet-stat-grid">
            {Object.entries(active.stats).map(([key, value]) => (
              <div className="fleet-stat" key={key}>
                <small>{key}</small>
                <span className="fleet-stat-track">
                  <span className="fleet-stat-fill" key={`${active.id}-${key}`} style={{ width: `${value}%` }} />
                </span>
                <b>{value}</b>
              </div>
            ))}
          </div>
          <div className="fleet-system-grid" aria-label="Fleet system selector">
            {systems.map((system) => (
              <button
                type="button"
                key={system.id}
                className={activeId === system.id ? "is-active" : ""}
                onClick={() => setActiveId(system.id)}
                data-testid={`fleet-system-${system.id}`}
                aria-pressed={activeId === system.id}
              >
                {system.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="fleet-scan-button"
            onClick={runDiagnostics}
            disabled={scanning}
            data-testid="run-diagnostics-button"
          >
            <Radar size={15} />
            {scanning ? "DIAGNOSTIC SWEEP RUNNING…" : "RUN FULL DIAGNOSTICS"}
          </button>
          {scanResult ? (
            <p className="fleet-scan-result" data-testid="scan-result">
              {scanResult}
            </p>
          ) : null}
        </div>
      </Reveal>

      <Reveal className="fleet-feed" delay={0.14} data-testid="fleet-feed">
        <span className="fleet-feed-label">
          <Activity size={13} /> FLEET ACTIVITY — {stamp} SECTOR TIME
        </span>
        <ul>
          {feed.map((entry, index) => (
            <li key={`${entry}-${index}`} data-testid={index === 0 ? "fleet-feed-latest" : undefined}>
              {entry}
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
}
